import React, { useState } from "react";
import {
  PaymentElement,
  LinkAuthenticationElement,
  AddressElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";

import { Config } from "@eagerdog/config";
import { IAddress, ITransaction } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./DonationForm.module.scss";

interface IProps {
  paymentIntentId?: string
  disableSubmit: boolean
  handleModalClose: () => void
  onSubmit: (
    paymentIntentId: string,
    donorName: string,
    donorEmail: string,
    donorAddress: IAddress,
    donorPhone: string
  ) => Promise<ITransaction>
  onError: (error?: any) => void
  presetAmounts?: number[]
  donationAmount: number
  customDonationAmount?: number
  updatingIntent: boolean
  formatCurrency: (amount: number) => string
  handleAmountChange: (amount: number) => void
  handleCustomAmountChange: (event: any) => void
}

const DonationForm: React.FC<IProps> = ({
  paymentIntentId,
  disableSubmit,
  handleModalClose,
  onSubmit,
  onError,
  presetAmounts,
  donationAmount,
  customDonationAmount,
  updatingIntent,
  formatCurrency,
  handleAmountChange,
  handleCustomAmountChange
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const user = helperService.getUser();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(user?.email);

  const handleLinkAuthenticationChange = async (event: any) => {
    setEmail(event.value?.email);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    const addressElement = elements.getElement(AddressElement);
    const addressResult = await addressElement?.getValue();

    if (addressResult?.complete) {
      const { name, address, phone } = addressResult.value;

      // first we need to set intent id on transaction metadata
      // in order to have it on webhook event listener
      if (paymentIntentId) {
        onSubmit(
          paymentIntentId,
          name,
          email,
          address as IAddress,
          phone ?? "",
        )
          .then(async () => {
            const { paymentIntent, error } = await stripe.confirmPayment({
              elements,
              confirmParams: {
                return_url: Config.web.REACT_APP_WEB_URL
              },
              redirect: "if_required"
            });

            if (error) {
              onError(error);
            } else if (paymentIntent) {
              toast.show({
                title: "Donation",
                content: "Successful donation. Thank you! We sent you an email with details.",
                duration: 5000,
                type: "success"
              });
              handleModalClose();
              // Redirect to account transactions page
              navigate("/account?t=4");
            }
          })
          .catch((error) => {
            toast.show({
              errorDetails: error,
              title: "Donation",
              content: "Could not process donation transaction, please try again.",
              duration: 5000,
              type: "fail"
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.DonationForm}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Amount</div>
        <div className={styles.amountsWrap}>
          {presetAmounts?.map((amount) => {
            return (
              <Button
                key={amount}
                disabled={updatingIntent}
                className={amount === donationAmount && !customDonationAmount ? styles.selectedAmount : ""}
                onClick={() => handleAmountChange(amount)}
              >
                {formatCurrency(amount)}
              </Button>
            );
          })}
        </div>

        <div className={styles.customAmountWrap}>
          <Input
            type="number"
            min={10}
            value={customDonationAmount}
            label="Custom donation amount"
            placeholder="Or enter donation amount"
            onChange={handleCustomAmountChange}
          />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Donor & Payment Information</div>
        <LinkAuthenticationElement
          options={{
            defaultValues: {
              email,
            }
          }}
          onChange={handleLinkAuthenticationChange}
        />
        <AddressElement
          options={{
            mode: "billing",
            fields: {
              phone: "always",
            },
            validation: {
              phone: {
                required: "always",
              },
            },
            defaultValues: {
              name: user.display_name,
              address: {
                country: user.address?.country === "Canada" ? "CA" : "US",
                line1: user.address?.line1,
                line2: user.address?.line2,
                city: user.address?.city,
                state: user.address?.state,
                postal_code: user.address?.postal_code,
              },
              phone: user.phone,
            },
          }}
        />

        <PaymentElement/>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          disabled={disableSubmit}
          type="submit"
          isLoading={loading}
        >
          Donate
        </Button>
      </div>
    </form>
  )
}

export { DonationForm }
